"use client";

import { CountryInfo } from "@/store/types/countryTypes";
import React from "react";

const CountryCostOfLiving: React.FC<{ country: CountryInfo }> = ({ country }) => {
  const sectionData = country?.costOfLiving;

  const livingRows = sectionData?.livingTable || [];
  const cityCards = sectionData?.cityCosts || [];
  const ruleTitle = sectionData?.ruleOfThumb?.title;
  const ruleDesc = sectionData?.ruleOfThumb?.description;

  if (livingRows.length === 0 && cityCards.length === 0 && !ruleDesc) {
    return null;
  }

  const heading =
    sectionData?.heading || `Cost of Living in ${country.countryName}`;
  const subheading = sectionData?.subheading;

  const cityHeading = sectionData?.cityCostsHeading || "Cost of Living by City";

  return (
    <div className="w-full my-12 space-y-8">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2233] ff_poppins">
          {heading}
        </h2>
        {subheading && (
          <p className="mt-1.5 text-xs sm:text-sm text-[#667085] ff_poppins">
            {subheading}
          </p>
        )}
      </div>

      {/* TOP SECTION: TABLE LEFT + RULE OF THUMB RIGHT */}
      {(livingRows.length > 0 || ruleDesc) && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* TABLE LEFT */}
          {livingRows.length > 0 && (
            <div className={`${ruleDesc ? "lg:col-span-7" : "lg:col-span-12"} rounded-[18px] overflow-hidden border border-[#E0D8FF] bg-white shadow-sm`}>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#0A1629] text-white uppercase text-xs sm:text-sm font-semibold tracking-wider">
                      <th className="py-4 px-6">Expense</th>
                      <th className="py-4 px-6">Avg. Cost / Month</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs sm:text-sm text-[#333E5C] ff_poppins font-medium">
                    {livingRows.map((row, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-white" : "bg-[#FBF9FF]"}
                      >
                        <td className="py-3.5 px-6 font-semibold text-[#1C2233]">
                          {row.expense}
                        </td>
                        <td className="py-3.5 px-6 font-bold text-[#0A1629]">
                          {row.avgCost}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* RULE OF THUMB RIGHT */}
          {ruleDesc && (
            <div className={`${livingRows.length > 0 ? "lg:col-span-5" : "lg:col-span-12"} bg-gradient-to-r from-[#FFF5F2] to-[#FFF9F5] border border-[#FFDCD2] rounded-[18px] p-6 sm:p-7 shadow-xs h-full flex flex-col justify-center`}>
              {ruleTitle && (
                <h4 className="text-sm sm:text-base font-bold text-[#1C2233] ff_poppins mb-2">
                  {ruleTitle}
                </h4>
              )}
              <p className="text-xs sm:text-sm text-[#525B73] ff_poppins leading-relaxed">
                {ruleDesc}
              </p>
            </div>
          )}
        </div>
      )}

      {/* BOTTOM SECTION: CITY COSTS GRID */}
      {cityCards.length > 0 && (
        <div className="space-y-4 pt-4">
          <h3 className="text-xl font-bold text-[#1C2233] ff_poppins">
            {cityHeading}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cityCards.map((city, idx) => (
              <div
                key={idx}
                className="bg-white rounded-[18px] border border-[#E0D8FF] p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow space-y-2"
              >
                <h4 className="text-base font-bold text-[#1C2233] ff_poppins">
                  {city.cityName}
                </h4>
                <p className="text-lg font-extrabold text-[#2B7FFF] ff_poppins">
                  {city.monthlyCost}
                  <span className="text-xs font-medium text-[#667085]">/month</span>
                </p>
                {city.description && (
                  <p className="text-xs sm:text-sm text-[#525B73] ff_poppins leading-relaxed">
                    {city.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCostOfLiving;

